import {Paginated} from "./Users";
import {SubAPI} from "./SubAPI";
import {FileInfo} from "./Files"
import {DateString, ProjectStage} from "./Projects";
import {Div10Spec} from "./Pricing";

export type RelatedFileType = "custom-preps"

export class Products extends SubAPI {
    list(input: {
        productCode?: string;
        name?: string;
        manufacturer?: number;
        category?: number;
        offset?: number;
        forProductSettings?: boolean
        kind?: "hardware" | "div-10" | "frame-anchor" | "component";
    }) {
        return this.fetcher.get<Paginated<Product>>("/api/products", input)
    }

    makeChildrenOf(props: {
        newParent: number;
        products: number[];
    }) {
        return this.fetcher.post("/api/product/make-children-of", props)
    }

    discontinuedForProject(input: {project: number}) {
        return this.fetcher.get<Product[]>("/api/product/discontinued-for-project", input)
    }

    dynamicErrors(input: {project: number}) {
        return this.fetcher.get<DynamicProductError[]>("/api/product/dynamic-errors", input)
    }

    relatedFiles(input: {
        product: number;
    }) {
        return this.fetcher.get<FileInfo[]>("/api/products/related-files", input)
    }

    async search(input:
        ({ productSearch?: string; } | { productCodeSearch?: string; }) &
        {
            id?: number;
            preferMfg?: number;
            preferFinish?: string;
            category?: number;
            kind?: "hardware" | "div-10" | "hardware,div-10" | "frame-anchor"|"manufacture";
            offset?: number;
            renderProductForOpening?: number;
            renderProductForQuoteOpening?: number;
        }, canceler?: AbortController) {

        const search = new URLSearchParams();
        for(let k in input) {
            const v = input[k];
            if(v) {
                search.set(k, v.toString());
            }
        }

        const res = await this.fetcher.fetch("/api/products/search?" + search, {
            method: "GET",
            signal: canceler?.signal,
        });

        return this.fetcher.handleFetchResponse<Paginated<ProductSearchResult>>(res)
    }

    searchSignal(input: {
        product: number;
        searchQuery: string;
    }) {
        return this.fetcher.post("/api/products/search-signal", input)
    }

    categories(input: {
        offset?: number;
        kind?: "div-10" | "hardware"
        parent?: string
    }) {
        return this.fetcher.get<Paginated<Category>>("/api/product/categories", input)
    }

    upsert(input: {
        id?: number;
        productCode: string;
        series: string;
        manufacturer: number;
        name: string;
        finish: string;
        category: number;
        defaultQty: number;

        updateQuotePricingForProject?: number

        setProductScripts?: ProductScript[]

        reassignInventoryToNewCodes?: boolean
    }) {
        return this.fetcher.post<{product: Product, updateMeta: ProductUpdateMeta}>("/api/product", input)
    }

    upsertWithTask(input: {
        id?: number;
        productCode: string;
        series: string;
        manufacturer: number;
        name: string;
        finish: string;
        category: number;
        defaultQty: number;

        updateQuotePricingForProject?: number

        setProductScripts?: ProductScript[]

        updateDefaultSupplierForOtherProductsInCategory?: boolean

        reassignInventoryToNewCodes?: boolean
    }) {
        return this.fetcher.post<{task: string}>("/api/product/with-task", input)
    }

    triggerProjectRecalc(input: {
        product: number;
    }) {
        return this.fetcher.post<ProductUpdateMeta>("/api/product/trigger-project-recalc", input)
    }

    stockingOptions(input: {
        product: number;
    }) {
        return this.fetcher.get<{
            type: "list";
            list: Product[]
        }>("/api/product/stocking-options", input)
    }

    upload(props: {
        file: File;
        id: number;
    }) {
        return this.fetcher.postFormData<{id: number}>("/api/div-10/set-cut-sheet", {
            file: props.file,
            info: JSON.stringify({id: props.id}),
        });
    }

    listById(input: { ids: number[] }) {
        return this.fetcher.post<Product[]>("/api/products", input)
    }

    getById(id: number) {
        return this.listById({ids: [id]}).then(v => v[0]);
    }

    getFinishNameForCode(input: { code: string }) {
        return this.fetcher.get<{finishName: string}>("/api/product/finish-name", input)
    }

    buildDoorSpec(input: {
        manufacturer: number;
        width: string;
        height: string;
        thickness: string;
        finish: string;
        material: string;
        series: string;
        gauge: string;
        elevation: string;
        core: string;
        seam: string;
        handing: string;
        label: string;
        preps: string[];
    }) {
        return this.fetcher.post<Product>("/api/product/door-spec", input)
    }

    buildFrameSpec(input: {
        manufacturer: number;
        width: string;
        height: string;
        preps: string[];
        handing: string;
        material: string;
        series: string;
        gauge: string;
        profile: string;
        jambDepth: string;
        doorThickness: string;
        construction: string;
        type: string;
        category?: string;
        anchorQty: number;
    }) {
        return this.fetcher.post<Product>("/api/product/frame-spec", input)
    }

    listMeta(input: {
        product: number;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<ProductMetaLine>>("/api/product-meta/list", input)
    }

    listProjectUsage(input: {
        product: number;
    }) {
        return this.fetcher.get<ProductUsage[]>("/api/product/list-project-usage", input)
    }

    forSpec(input: Product) {
        return this.fetcher.post<Product>("/api/product/for-spec", input);
    }

    listScripts(input: {
        product: number;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<ProductScript>>("/api/product/scripts", input);
    }

    checkScript(input: {
        script: string
    }) {
        return this.fetcher.post<{
            status: "error" | "ok",
            detail: string;
            line: number;
            context: string;
        }>("/api/product/script/check", input);
    }

    listFinishes(input: {offset: number}) {
        return this.fetcher.get<Paginated<FinishPreset>>("/api/product/finishes", input)
    }

    updateFinish(input: FinishPreset) {
        return this.fetcher.post("/api/product/finish", input)
    }

    listSummaryForProjectRecalc(input: {
        project: number;
        alternative?: number;
    }) {
        return this.fetcher.get<RecalcSummaryItem[]>("/api/product/recalc-summary/for-project", input)
    }

    listForProjectRecalc(input: {
        project: number;
        alternative?: number;
    }) {
        return this.fetcher.get<RecalcItem[]>("/api/product/recalc-items/for-project", input)
    }

    recalcForProject(input: {
        project: number;
        alternative?: number;
        skip: RecalcItem[];
    }) {
        return this.fetcher.post<{task: string}>("/api/product/recalc-items/for-project", input)
    }

    listRecalcHistory(input: {
        project: number;
        alternative?: number;
    }) {
        return this.fetcher.get<RecalcHistoryItem[]>("/api/product/recalc-history", input)
    }

    listRecalcPriceChanges(input: {
        log: number;
    }) {
        return this.fetcher.get<RecalcPriceChangeItem[]>("/api/product/recalc-history/prices", input)
    }

    listRecalcProductChanges(input: {
        log: number;
    }) {
        return this.fetcher.get<RecalcProductChangeItem[]>("/api/product/recalc-history/products", input)
    }

    listRecalcOpenings(input: {
        log: number;
    }) {
        return this.fetcher.get<RecalcOpeningChange[]>("/api/product/recalc-history/openings", input)
    }
}

export type DynamicProductError = {
    id: number;
    openingName: string;
    hardwareGroupName: string;
    productCode: string;
    productCategory: string;
    error: string;
}

export type RecalcSummaryItem = {
    id: number;
    product: number;

    categoryName: string;
    productCode: string;
    finishCode: string;

    changes: RecalcProductMeta[] | null
    openingsAffected: number;
    changesAffected: number;
}

export type RecalcProductMeta = {
    id: number;
    description: string;
    meta: ProductChangeMeta | null
}

export type ProductChangeMeta = {
    [k: string]: {
        old: any;
        new: any;
    } | null;
};

export type RecalcItem = {
    id: number;
    product: number;

    categoryName: string;
    productCode: string;
    finishCode: string;

    alternativeName?: string;
    openingName?: string;
    freeformChange?: number;
    qty: number;

    doorActiveStatus: string | null;
    doorInactiveStatus: string | null;
    frameStatus: string | null;

    error?: string | null;
}

export type RecalcHistoryItem = {
    id: number;
    createdAt: DateString;
    createdByName: string | null;
    successful: boolean;
    errorText: string | null;
}

export type RecalcProductChangeItem = {
    id: number;
    product: number;
    categoryName: string;
    productCode: string;
    finishCode: string;
    changes: {
        id: number;
        description: string;
        meta: ProductChangeMeta | null;
    }[];
}

export type RecalcOpeningChange = {
    id: number;
    name: string;
}

export type RecalcPriceChangeItem = {
    id: number;
    quotePrice: number;
    productType: string;
    itemName: string;
    prevQty: number;
    prevExtendedPrice: number;
    afterQty: number;
    afterExtendedPrice: number;
    removed: boolean;
    added: boolean;
}

export type FinishPreset = {
    id: number;
    code: string;
    description: string;
}

export type ProductUpdateMeta = {
    skippedQuoteBecauseProjectModeChanged: ProductUpdateMetaItem[] | null;
    skippedBecauseProjectModeNotActive: ProductUpdateMetaItem[] | null;
    skippedBecauseProjectArchived: ProductUpdateMetaItem[] | null;
    skippedBecauseDelivered: ProductUpdateMetaItem[] | null;
    skippedBecauseOrdered: ProductUpdateMetaItem[] | null;

    updated: ProductUpdateMetaItem[] | null;
}

export type ProductUpdateMetaItem = {
    id: number;
    project: number;
    projectName: string;
    projectNumber: number;
    openings: string[];
}

export type ProductMetaLine = {
    id: number;
    description: string;
    product: number;
    createdAt: DateString
    createdBy: number;
    createdByName: string;

    metaJson: ProductChangeMeta | null;
}

export type Category = {
    id: number;
    name: string;
    sortOrder: number | null;
    parentCategory?: number;
    hideInProductEditor: boolean;
    kind: "hardware" | "div-10"
}

export type Product = {
    id: number;
    parentProduct: number;

    inventoryItem: number | null;
    warehouseAvailableQty: number;

    hideFromSearch: boolean;
    isStocking: boolean;

    name: string;
    productCode: string;
    series: string;
    finish: string;
    finishName: string;

    defaultQty: number;

    defaultSupplierCategory: number;
    defaultSupplierCategoryName: string;

    partOfFrame: boolean;

    category: number;
    categoryName: string;
    kind: "hardware" | "div-10" | "frame-anchor";

    manufacturer: number;
    manufacturerName: string;
    supplier: number;
    supplierName: string;
    defaultDistributor: number | null;
    defaultDistributorName: string | null;

    defaultHwGroup: OpeningType2;

    hwPrepKey: string;

    hwPrepSingleFrame: string;
    hwPrepSingleFrameCustomTemplate?: number;
    hwPrepSingleFrameCustomTemplateName?: string;
    hwPrepSingleActiveDoor: string;
    hwPrepSingleActiveDoorCustomTemplate?: number;
    hwPrepSingleActiveDoorCustomTemplateName?: string;
    hwPrepPairFrame: string;
    hwPrepPairFrameCustomTemplate?: number;
    hwPrepPairFrameCustomTemplateName?: string;
    hwPrepPairActiveDoor: string;
    hwPrepPairActiveDoorCustomTemplate?: number;
    hwPrepPairActiveDoorCustomTemplateName?: string;
    hwPrepPairInactiveDoor: string;
    hwPrepPairInactiveDoorCustomTemplate?: number;
    hwPrepPairInactiveDoorCustomTemplateName?: string;
    hwPrepPairBothActiveDoor: string;
    hwPrepPairBothActiveDoorCustomTemplate?: number;
    hwPrepPairBothActiveDoorCustomTemplateName?: string;

    keyed: boolean;

    frameSpec?: number;
    doorSpec?: number;
    div10Spec?: number;

    unitSystem: string;

    dimensionFormula?: number;

    perLengthPurchaseIncrement: string;
    perLengthInventoryIncrement: string | null;

    dimLengthFormula?: string;
    dimLengthRoundType?: RoundType;
    dimLengthRoundInterval?: string;

    dimWidthFormula?: string;
    dimWidthRoundType?: RoundType;
    dimWidthRoundInterval?: string;

    dimHeightFormula?: string;
    dimHeightRoundType?: RoundType;
    dimHeightRoundInterval?: string;

    dimensions: string;
    dimWidth: string;
    dimHeight: string;
    dimLength: string;

    catalogs: {
        catalog: number;
        name: string;
        pages: string
    }[];

    isAutoQtyHinge: boolean;

    defaultCostCents?: number;
    lastUnitCostCents?: number

    door: DoorSpec | null;
    frame: FrameSpec | null;
    div10: Div10Spec | null;

    updatedAt: DateString;
    updatedBy?: number;
    updatedByName?: string;

    discontinued: boolean;
    discontinuedReplaceWith: DiscontinuedProduct[];

    setProductScripts?: ProductScript[]
    ignoreCatalogUpdatesForProjectReview?: boolean;

    reviewStatus?: ProductReviewStatus;
}

export type ProductReviewStatus = "complete" | "pending"

export type ProductScript = {
    id: number;
    name: string;
    script: string;
    archived: boolean;
}

export type DiscontinuedProduct = {
    id: number;
    manufacturer: number
    manufacturerName: string
    categoryName: string
    productCode: string
    finishName: string
    finish: string
}

export type DoorSpec = {
    core: string
    elevation: string;
    elevationFile?: number
    liteKitProfileFile?: number;
    finish: string
    gauge: string
    handing: string;
    height: string;
    label: string;
    material: string;
    seam: string;
    series: string
    thickness: string
    width: string
    woodDetail?: WoodDetail | null;
    preps?: Preps | null
}

export type WoodDetail = {
    materialName: string;
    cut: string;
    matching: string;
    matchingMethod: string;
    ply: string;
    finish: string;
    proofFile: number | null;
    swatchName: string;
}

export type FrameSpec = {
    series: string;
    material: string;
    gauge: string;
    screenElev: string;
    screenElevFile?: number;
    profile: string;
    jambDepth: string;
    doorThickness: string;
    construction: string;
    handing: string;
    label: string;
    type: string;
    width: string;
    height: string;
    preps?: Preps | null
}

export type Preps = {
    [key: string]: PrepDetail
} & Partial<{
    "customOffsets": PrepDetail
    "frameAnchors": PrepDetail
    "cutDown": PrepDetail
    "unequalPairDoorSizes": PrepDetail
}>;

export type PrepsEnriched = {
    [key: string]: PrepDetailEnriched
} & Partial<{
    "customOffsets": PrepDetailEnriched
    "frameAnchors": PrepDetailEnriched
    "cutDown": PrepDetailEnriched
    "unequalPairDoorSizes": PrepDetailEnriched
}>

export type PrepConfigCustomOffset = {
    value: string;
    name: string // e.g. a, aStar... see PrepsManager.ts
    enabled: boolean;
}

export type PrepDetail = {
    type: "standard"
    isHinge: boolean;

    hingeFrameActiveIndexes?: number[];
    hingeFrameInActiveIndexes?: number[];
    hingeDoorIndexes?: number[];

    frameSide?: "active" | "inactive";
} | {
    type: "custom"
    file: number;
    product: number;
    productName?: string;
    isHinge: boolean;

    hingeFrameActiveIndexes?: number[];
    hingeFrameInActiveIndexes?: number[];
    hingeDoorIndexes?: number[];

    frameSide?: "active" | "inactive";
} | {
    type: "frameAnchors",
    qty: number,
    name: "P&S" | "IMP" | "S&T" | "";
} | {
    type: "customOffsets"
    customOffsets: PrepConfigCustomOffset[];
    // a secondary door slot's own entry on a multi-opening frame (key "customOffsets@<slot>").
    slot?: string;
} | {
    type: "cutDown";
    cutDown: PrepConfigCutDown;
} | {
    type: "unequalPairDoorSizes",
    activeDoorWidth: string;
    inactiveDoorWidth: string;
    // a secondary door slot's own entry on a multi-opening frame (key "unequalPairDoorSizes@<slot>").
    slot?: string;
}

export type PrepConfigCutDown = {
    overCut: string | null;
    underCut: string | null;
    name: string;
}

export type PrepDetailEnriched = {
    type: "standard"
    isHinge: boolean;
    frameSide?: "active" | "inactive";
} | {
    type: "custom"
    file: number;
    product: number;
    productName?: string;
    productCategory?: string;
    productCategorySort?: number;
    productCategory1?: string;
    productCategory2?: string;
    productCategory3?: string;
    isHinge: boolean;
    frameSide?: "active" | "inactive";
    degreesOfOpening?: number;
} | {
    type: "frameAnchors",
    qty: number,
    name: "P&S" | "IMP" | "S&T" | "";
} | {
    type: "customOffsets"
    customOffsets: PrepConfigCustomOffset[];
    // a secondary door slot's own entry on a multi-opening frame (key "customOffsets@<slot>").
    slot?: string;
} | {
    type: "cutDown";
    cutDown: PrepConfigCutDown;
} | {
    type: "unequalPairDoorSizes",
    activeDoorWidth: string;
    inactiveDoorWidth: string;
    // a secondary door slot's own entry on a multi-opening frame (key "unequalPairDoorSizes@<slot>").
    slot?: string;
}

export type RoundType = "round-up-by-list-interval" | "round-up-by-fixed-interval" | "none";

export type OpeningType = "active" | "inactive" | "common";
export type OpeningType2 = OpeningType | "each-door";

export type ProductSearchResult = {
    id: number;
    productCode: string;
    series: string;
    categoryName: string;
    name: string;
    finish: string;
    manufacturerName: string;
    fuzzy?: boolean;

    discontinued: boolean;
    discontinuedReplaceWith: DiscontinuedProduct[];
}

export function fixFinishName(name: string | undefined | null) {
    if(name === "N/A") return "";
    return name;
}

export type ProductUsage = {
    id: number;
    project: number;
    projectName: string;
    projectNumber: number;
    projectStage: ProjectStage;
    qty: number;
    avgUnitCostCents: number | null;
    avgMarkupPercent: number | null;
}